import type React from 'react';
import type { RemainingItemsMetadataT } from '../../types';
export const ITEM_CATEGORY = ({
  PENDING: 'pending',
  ERROR: 'error'
} as const);
export type ItemCategory = (typeof ITEM_CATEGORY)[keyof typeof ITEM_CATEGORY];
export const OBJECT_TYPE = ({
  SLOT: 'slot',
  ANNOTATION: 'annotation',
  TASK: 'task',
  TASK_QUESTIONNAIRE: 'task questionnaire',
  QUESTIONNAIRE_FIELD: 'questionnaire-field',
  AUDIO_NOT_LISTENED: 'audio-not-listened',
  RULE_ENGINE: 'rule-engine'
} as const);
export type ObjectType = (typeof OBJECT_TYPE)[keyof typeof OBJECT_TYPE] | string;
export const TAG_LABEL = ({
  SPELLING: 'Spelling',
  ML: 'ML',
  SOP: 'SOP',
  TAG: 'Tag',
  RULE_ENGINE: 'Rule Engine',
  QUESTIONNAIRE: 'Questionnaire',
  MISSING_REQUIRED: 'Missing Required Label',
  QUADRANT_IMBALANCE: 'Quadrant Imbalance',
  MINIMUM_ANNOTATIONS: 'Minimum Annotations',
  MAX_INSTANCES: 'Max Instances'
} as const);

// Add new grouping keys here when porting additional labeling types.
export const REMAINING_ITEMS_GROUPING_KEY = ({
  SPELL_CHECK: 'spellCheck',
  SOP: 'sop',
  ML_FORMAT_WARNING: 'mlFormatWarning',
  MISSING_REQUIRED: 'missingRequired',
  INSTANCE_COUNT: 'instanceCount',
  RULE_ENGINE: 'ruleEngine',
  QUESTIONNAIRE: 'questionnaire',
  QUADRANT_IMBALANCE: 'quadrantImbalance',
  MINIMUM_ANNOTATIONS: 'minimumAnnotations',
  OVERLAP_ASSIGNMENT: 'overlapAssignment'
} as const);
export type GroupingKeyType = (typeof REMAINING_ITEMS_GROUPING_KEY)[keyof typeof REMAINING_ITEMS_GROUPING_KEY] | (string & {});
export type TagColor = {
  primary: string;
  secondary?: string;
  tertiary?: string;
};
export type RemainingItem = {
  id: string;
  label: string;
  objectType: ObjectType;
  category: ItemCategory;
  title?: string | React.ReactNode;
  description: string | React.ReactNode;
  tagLabel?: string;
  tagColor?: TagColor;
  groupingKey?: GroupingKeyType;
  metadata?: RemainingItemsMetadataT;
  customActions?: React.ReactNode;
};
export type NavigationConfig = {
  canNavigate: (item: RemainingItem) => boolean;
  onNavigate: (item: RemainingItem) => void;
  shouldKeepPanelOpen?: (item: RemainingItem) => boolean;
};
export type ActionRenderer = (item: RemainingItem) => React.ReactNode | null;
export type ItemGroupProps = {
  label: string;
  itemCount: number;
  tagColor?: TagColor;
  children: React.ReactNode;
  styleOverrides?: {
    hideBorderTop?: boolean;
  };
};
export type ItemCardProps = {
  item: RemainingItem;
  navigationConfig: NavigationConfig;
  renderItemActions?: ActionRenderer;
  isLast: boolean;
};
export type GroupingConfig = {
  tagMap?: Record<string, string>;
  showTaskLevelTags?: boolean;
  taskLevelTagMap?: Record<string, string>;
  taskGroupLabel?: string;
  taskLevelObjectTypes?: Set<string>;
};
export type GroupedItems = {
  groupKey: string;
  groupLabel: string;
  itemCount: number;
  items: RemainingItem[];
  tagColor?: TagColor;
  objectType: string;
};
export type ValidationState = {
  isRunning: boolean;
  message?: string;
};
export type RemainingItemsPanelProps = {
  items: RemainingItem[];
  itemCount: number;
  navigationConfig: NavigationConfig;
  renderItemActions?: ActionRenderer;
  groupingConfig?: GroupingConfig;
  validationState?: ValidationState;
};